import React, { useState, useMemo } from "react";
import {
  Container,
  Table,
  Row,
  Col,
  Input,
  Button,
  Modal,
  Text,
  Spacer,
  Card,
  styled,
} from "@nextui-org/react";
import { AuditLogEntry, FilterState } from "./types";
import { mockAuditLogs } from "./mockData";

const StyledBadge = styled("span", {
  display: "inline-block",
  textTransform: "uppercase",
  padding: "$2 $3",
  margin: "0 2px",
  fontSize: "10px",
  fontWeight: "$bold",
  borderRadius: "14px",
  letterSpacing: "0.6px",
  lineHeight: 1,
  boxShadow: "1px 2px 5px 0px rgb(0 0 0 / 5%)",
  alignItems: "center",
  alignSelf: "center",
  color: "$white",
});

const columns = [
  { name: "TIMESTAMP", uid: "timestamp" },
  { name: "USER", uid: "user" },
  { name: "ACTION", uid: "action" },
  { name: "ENVIRONMENT", uid: "environment" },
  { name: "DETAILS", uid: "details" },
];

const AuditLogs: React.FC = () => {
  const [logs] = useState<AuditLogEntry[]>(mockAuditLogs);
  const [filters, setFilters] = useState<FilterState>({
    startDate: "",
    endDate: "",
    user: "",
  });
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const logDate = new Date(log.timestamp);
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      // Adjust end date to include the whole day
      if (endDate) {
        endDate.setHours(23, 59, 59, 999);
      }

      const matchesUser = log.user
        .toLowerCase()
        .includes(filters.user.toLowerCase());
      const matchesStartDate = startDate ? logDate >= startDate : true;
      const matchesEndDate = endDate ? logDate <= endDate : true;

      return matchesUser && matchesStartDate && matchesEndDate;
    });
  }, [logs, filters]);

  const openModal = (log: AuditLogEntry) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLog(null);
  };

  const renderDetails = (data: any) => {
    if (data === null || data === undefined) return "N/A";
    return JSON.stringify(data, null, 2);
  };

  const renderCell = (log: AuditLogEntry, columnKey: React.Key) => {
    switch (columnKey) {
      case "timestamp":
        return new Date(log.timestamp).toLocaleString();
      case "user":
        return log.user;
      case "action":
        return log.action;
      case "environment":
        return (
          <StyledBadge
            css={{
              bg:
                log.environment === "Prod"
                  ? "$errorLight"
                  : log.environment === "QA"
                    ? "$warningLight"
                    : "$primaryLight",
              color:
                log.environment === "Prod"
                  ? "$error"
                  : log.environment === "QA"
                    ? "$warning"
                    : "$primary",
            }}
          >
            {log.environment}
          </StyledBadge>
        );
      case "details":
        return (
          <Button size="xs" auto onPress={() => openModal(log)}>
            View Details
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Spacer y={1} />
      <Text h2>Audit Logs</Text>
      <Spacer y={1} />

      {/* Filters */}
      <Card variant="bordered">
        <Card.Body>
          <Row gap={1} align="flex-end" wrap="wrap">
            <Col css={{ mw: "200px" }}>
              <Input
                label="Start Date"
                type="date"
                fullWidth
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </Col>
            <Col css={{ mw: "200px" }}>
              <Input
                label="End Date"
                type="date"
                fullWidth
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </Col>
            <Col css={{ mw: "200px" }}>
              <Input
                label="Administrator"
                placeholder="Search by user..."
                fullWidth
                value={filters.user}
                onChange={(e) => handleFilterChange("user", e.target.value)}
              />
            </Col>
            <Col css={{ mw: "100px" }}>
              <Button
                auto
                color="secondary"
                onPress={() => setFilters({ startDate: "", endDate: "", user: "" })}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Spacer y={1} />

      {/* Table */}
      <Table
        aria-label="Audit Logs Table"
        css={{
          height: "auto",
          minWidth: "100%",
        }}
        selectionMode="none"
      >
        <Table.Header columns={columns}>
          {(column) => (
            <Table.Column key={column.uid}>{column.name}</Table.Column>
          )}
        </Table.Header>
        <Table.Body items={filteredLogs}>
          {(item: AuditLogEntry) => (
            <Table.Row key={item.id}>
              {(columnKey: React.Key) => <Table.Cell>{renderCell(item, columnKey)}</Table.Cell>}
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      {/* Details Modal */}
      <Modal
        closeButton
        aria-labelledby="modal-title"
        open={isModalOpen}
        onClose={closeModal}
        width="800px"
      >
        <Modal.Header>
          <Text id="modal-title" size={18}>
            Audit Log Details
          </Text>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <>
              <Row>
                <Col>
                  <Text b>Action:</Text> {selectedLog.action}
                </Col>
                <Col>
                  <Text b>User:</Text> {selectedLog.user}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text b>Timestamp:</Text> {new Date(selectedLog.timestamp).toLocaleString()}
                </Col>
                <Col>
                  <Text b>Environment:</Text> {selectedLog.environment}
                </Col>
              </Row>
              <Spacer y={1} />
              <Row>
                <Col>
                  <Text h4>Previous State</Text>
                  <Card variant="bordered" css={{ bg: "$gray100", p: "$5", mh: "300px", overflow: "auto" }}>
                    <pre style={{ margin: 0 }}>
                      {renderDetails(selectedLog.details.previousState)}
                    </pre>
                  </Card>
                </Col>
                <Spacer x={1} />
                <Col>
                  <Text h4>New State</Text>
                  <Card variant="bordered" css={{ bg: "$gray100", p: "$5", mh: "300px", overflow: "auto" }}>
                    <pre style={{ margin: 0 }}>
                      {renderDetails(selectedLog.details.newState)}
                    </pre>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button auto flat color="error" onPress={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AuditLogs;
